import { __decorate } from "tslib";
import { html } from 'lit';
import { CipublicElement } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/mixins/base.js';
import { property, queryAssignedNodes, state } from 'lit/decorators.js';
import { emit } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/utils/event.js';
import { ResizeMixin } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/mixins/resize-observer.js';
import { InternalI18n } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/i18n/internal-i18n-controller.js';
import { locales } from './locales.js';
import '@cigna/cigna_csg_gsg_cipublic-button/cipublic-button.js';
import styles from './accordion.scss.js';
/**
 * cipublic-accordion
 *
 * @slot item - `cipublic-accordion-item`
 * @slot heading
 * @slot headingSummary
 * @slot content
 * @slot showText
 * @slot hideText
 */
export class Accordion extends ResizeMixin(CipublicElement) {
    constructor() {
        super(...arguments);
        this.i18n = new InternalI18n(this, locales);
        this.variant = 'standard';
        this.emitConnectedCallback = false;
    }
    connectedCallback() {
        super.connectedCallback();
        if (this.emitConnectedCallback) {
            emit(this, 'connected-callback');
        }
        this.addEventListener('accordionToggle', (e) => {
            const { detail } = e;
            if (detail.open === 'true') {
                this.toggleAccordion(detail.aindex, 'close');
            }
            else {
                this.toggleAccordion(detail.aindex, 'open');
            }
        });
    }
    async firstUpdated(changedProperties) {
        super.firstUpdated(changedProperties);
        await this.updateComplete;
        this.accordionItem = [];
        this.accordionHeading = [];
        this.headingSummary = [];
        this.accordionContent = [];
        [].forEach.call(this.accordionSlots, (element) => {
            this.accordionItem.push(element);
            this.accordionHeading.push(element.shadowRoot.querySelector('.accordion-header-button'));
            this.headingSummary.push(element.shadowRoot.querySelector('.headingSummary'));
            this.accordionContent.push(element.shadowRoot.querySelector('.content'));
        });
        for (let i = 0; i < this.accordionItem.length; i++) {
            const accordionItem = this.accordionItem[i];
            const accordionHeading = this.accordionHeading[i];
            const headingSummary = this.headingSummary[i];
            const accordionContent = this.accordionContent[i].assignedNodes()[0];
            const accordionHeadingText = accordionHeading.querySelector('slot').assignedNodes()[0];
            accordionItem.setAttribute('aindex', `${i}`);
            if (headingSummary) {
                if (!accordionItem.hasAttribute('summaryexpand')) {
                    headingSummary.style.display = 'inline';
                }
            }
            if (accordionItem.hasAttribute('open')) {
                accordionContent.setAttribute('aria-hidden', 'false');
                accordionContent.removeAttribute('tabindex');
                accordionHeading.setAttribute('aria-expanded', 'true');
                this.toggleAccordion(i, 'open');
            }
            else {
                accordionContent.setAttribute('aria-hidden', 'true');
                accordionContent.setAttribute('tabindex', '-1');
            }
            if (this.variant === 'inline') {
                accordionHeadingText.innerHTML = !accordionItem.hasAttribute('open')
                    ? accordionItem.getAttribute('showText')
                    : accordionItem.getAttribute('hideText');
            }
        }
    }
    handleResize() {
        for (let a = 0; a < this.accordionHeading.length; a++) {
            this.toggleAccordion(a, 'resize');
        }
    }
    static get styles() {
        return [styles];
    }
    render() {
        if (this.variant === 'fullWidth') {
            return html `<slot name="global-header"></slot>${this.globalButtons && this.multiSelect
                ? html `<div class="button-container"><cipublic-button @click="${this.openAllAccordions}" size="default" variant="secondary" type="button" class="accordion-expand">${this.i18n.t('accordion_expand')}</cipublic-button><cipublic-button @click="${this.closeAllAccordions}" size="default" variant="secondary" type="button" class="accordion-expand">${this.i18n.t('accordion_collapse')}</cipublic-button></div>`
                : ''}<slot name="item"></slot>`;
        }
        else if (this.variant === 'standard') {
            return html `<slot name="global-header"></slot>${this.globalButtons && this.multiSelect
                ? html `<div class="button-container"><cipublic-button @click="${this.openAllAccordions}" size="small" variant="secondary" type="button" class="accordion-expand">${this.i18n.t('accordion_expand')}</cipublic-button><cipublic-button @click="${this.closeAllAccordions}" size="small" variant="secondary" type="button" class="accordion-expand">${this.i18n.t('accordion_collapse')}</cipublic-button></div>`
                : ''}<slot name="item"></slot>`;
        }
        else {
            return html `<slot name="global-header"></slot>${this.globalButtons && this.multiSelect
                ? html `<cipublic-button @click="${this.openAllAccordions}" size="default" variant="secondary" type="button" class="accordion-expand">${this.i18n.t('accordion_expand')}</cipublic-button><cipublic-button @click="${this.closeAllAccordions}" size="default" variant="secondary" type="button" class="accordion-expand">${this.i18n.t('accordion_collapse')}</cipublic-button>`
                : ''}<slot name="item"></slot>`;
        }
    }
    closeAllAccordions() {
        this.accordionHeading.forEach((_e, i) => this.toggleAccordion(i, 'close'));
    }
    openAllAccordions() {
        this.accordionHeading.forEach((_e, i) => this.toggleAccordion(i, 'open'));
    }
    toggleAccordion(i, action) {
        const accordionItem = this.accordionItem[i];
        const accordionHeading = this.accordionHeading[i];
        const headingSummary = this.headingSummary[i];
        const accordionContent = this.accordionContent[i];
        const accordionContentSlot = this.accordionContent[i].assignedNodes()[0];
        const accordionHeadingText = accordionHeading.querySelector('slot').assignedNodes()[0];
        if (action === 'resize') {
            if (accordionItem.hasAttribute('open'))
                accordionContent.style.maxHeight = `${accordionContentSlot.clientHeight}px`;
            return;
        }
        if (action === 'open') {
            if (!this.multiSelect) {
                for (let a = 0; a < this.accordionHeading.length; a++) {
                    if (i !== a)
                        this.toggleAccordion(a, 'close');
                }
            }
            accordionItem.setAttribute('open', 'true');
            accordionContent.style.maxHeight = `${accordionContentSlot.clientHeight}px`;
            if (headingSummary) {
                if (accordionItem.hasAttribute('summaryexpand')) {
                    headingSummary.style.display = 'inline';
                }
            }
        }
        else if (action === 'close') {
            accordionItem.removeAttribute('open');
            accordionContent.style.maxHeight = '0';
            if (headingSummary) {
                if (accordionItem.hasAttribute('summaryexpand')) {
                    headingSummary.style.display = 'none';
                }
            }
        }
        if (this.variant === 'inline') {
            const hideShowText = action === 'open' ? 'hideText' : 'showText';
            accordionHeadingText.innerHTML = accordionItem.getAttribute(hideShowText);
        }
        const ariaExpanded = action === 'close';
        accordionContentSlot.setAttribute('aria-hidden', `${ariaExpanded}`);
        accordionHeading.setAttribute('aria-expanded', `${!ariaExpanded}`);
        if (!ariaExpanded) {
            accordionContentSlot.removeAttribute('tabindex');
        }
        else {
            accordionContentSlot.setAttribute('tabindex', '-1');
        }
    }
}
__decorate([
    state()
], Accordion.prototype, "accordionItem", void 0);
__decorate([
    state()
], Accordion.prototype, "accordionHeading", void 0);
__decorate([
    state()
], Accordion.prototype, "headingSummary", void 0);
__decorate([
    state()
], Accordion.prototype, "accordionContent", void 0);
__decorate([
    queryAssignedNodes('item', true)
], Accordion.prototype, "accordionSlots", void 0);
__decorate([
    property({ type: String, reflect: true })
], Accordion.prototype, "variant", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], Accordion.prototype, "multiSelect", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], Accordion.prototype, "globalButtons", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], Accordion.prototype, "emitConnectedCallback", void 0);
//# sourceMappingURL=accordion.js.map